/*
 * Kivora bundle picker (snippets/kv-bundle-picker.liquid, block `kv_bundle` in main-product).
 *
 * Tier 1 = one item, tier 2 = a multi-buy (default 3, cheapest free via the automatic discount
 * "Buy 2, get the 3rd free"), optional tier 3 = a bigger multi-buy (default 6, 2 free: the same discount
 * repeats per 3 items). The picker only shows the saving; Shopify applies it in the cart.
 *
 * - Keeps the buy box's quantity input equal to the chosen tier, so Dawn's own Add to cart and the
 *   dynamic checkout button buy the right number of the selected variant.
 * - Items mode (data-items="true"): each tier card holds its own option pickers per item (e.g. #1 Colour +
 *   Size, #2 Colour + Size). Prices follow the picked variants, item #1 of the selected tier drives the
 *   (hidden) main variant picker so the gallery and price follow it, and Add to cart always adds the
 *   picked items. An optional end time (data-ends) removes the multi-buy tiers when the promo ends.
 * - If the shopper picks different variants per item in tier 2 or 3, Add to cart is taken over:
 *   one /cart/add.js call with every line, then Dawn's cart drawer is re-rendered from the response.
 */
(() => {
  if (customElements.get('kv-bundle-picker')) return;

  class KvBundlePicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.q1 = parseInt(this.dataset.q1, 10) || 1;
      this.q2 = parseInt(this.dataset.q2, 10) || 3;
      this.free = parseInt(this.dataset.free, 10) || 0;
      this.q3 = parseInt(this.dataset.q3, 10) || 0;
      this.free3 = parseInt(this.dataset.free3, 10) || 0;
      this.mix = this.dataset.mix === 'true';
      this.itemsMode = this.dataset.items === 'true';
      this.syncMain = this.dataset.syncMain === 'true';
      this.variants = [];
      const variantsNode = this.querySelector('[data-kvbp-variants]');
      if (variantsNode) {
        try {
          this.variants = JSON.parse(variantsNode.textContent);
        } catch (error) {
          this.variants = [];
        }
      }
      this.touchedUnits = false;
      this.busy = false;
      this.status = this.querySelector('[data-kvbp-status]');
      this.units = this.querySelector('[data-kvbp-units]');

      this.onChange = this.onChange.bind(this);
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.addEventListener('change', this.onChange);
      // Capture phase on document runs before Dawn's product-form submit listener.
      document.addEventListener('submit', this.onSubmitCapture, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || data.sectionId !== this.sectionId || !data.variant) return;
          this.onMainVariant(data.variant);
        });
      }

      this.syncTier();
      this.updatePrices();
      if (this.itemsMode) {
        this.refreshSwatches();
        this.scheduleEnd();
      }
    }

    disconnectedCallback() {
      if (this.endTimer) window.clearTimeout(this.endTimer);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('submit', this.onSubmitCapture, true);
      if (this.unsubscribeVariant) this.unsubscribeVariant();
    }

    /* ---------- state ---------- */

    tier() {
      const checked = this.querySelector('.kvbp__radio:checked');
      return checked ? checked.value : '1';
    }

    tierQty(tier = this.tier()) {
      if (this.itemsMode) {
        const card = this.card(tier);
        return card ? parseInt(card.dataset.qty, 10) || 1 : 1;
      }
      if (tier === '3' && this.q3) return this.q3;
      if (tier === '2') return this.q2;
      return this.q1;
    }

    tierFree(tier = this.tier()) {
      if (this.itemsMode) {
        const card = this.card(tier);
        return card ? parseInt(card.dataset.free, 10) || 0 : 0;
      }
      if (tier === '3' && this.q3) return this.free3;
      if (tier === '2') return this.free;
      return 0;
    }

    isMulti(tier = this.tier()) {
      if (this.itemsMode) return this.tierQty(tier) > 1;
      return tier === '2' || (tier === '3' && this.q3 > 0);
    }

    card(tier = this.tier()) {
      return this.querySelector(`.kvbp__card[data-kvbp-tier="${tier}"]`);
    }

    /* ---------- items mode ---------- */

    findVariant(values) {
      return this.variants.find((variant) => variant.options.every((value, index) => value === values[index]));
    }

    itemValues(item) {
      return Array.from(item.querySelectorAll('[data-kvbp-opt]'))
        .sort((a, b) => parseInt(a.dataset.kvbpOpt, 10) - parseInt(b.dataset.kvbpOpt, 10))
        .map((select) => select.value);
    }

    /** Variants picked in a tier card, one per item (null where the combination does not exist). */
    cardVariants(tier = this.tier()) {
      const card = this.card(tier);
      if (!card) return [];
      return Array.from(card.querySelectorAll('[data-kvbp-item]')).map((item) => this.findVariant(this.itemValues(item)) || null);
    }

    refreshSwatches(scope = this) {
      scope.querySelectorAll('.kvbp__opt--colour').forEach((wrap) => {
        const select = wrap.querySelector('select');
        const swatch = wrap.querySelector('[data-kvbp-swatch]');
        const option = select && select.selectedOptions[0];
        if (swatch && option && option.dataset.hex) swatch.style.setProperty('--kvbp-swatch', option.dataset.hex);
      });
    }

    /** Push item #1 of the selected tier into Dawn's (hidden) variant picker, so gallery, price and URL follow. */
    pushMain() {
      if (!this.syncMain) return;
      const variants = this.cardVariants();
      const first = variants[0];
      if (!first || String(first.id) === this.mainVariantId()) return;
      const picker = document.getElementById(`variant-selects-${this.sectionId}`);
      if (!picker) return;
      const groups = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown'));
      let last = null;
      first.options.forEach((value, index) => {
        const group = groups[index];
        if (!group) return;
        const select = group.querySelector('select');
        if (select) {
          if (select.value !== value) {
            select.value = value;
            last = select;
          }
          return;
        }
        const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((radio) => radio.value === value);
        if (input && !input.checked) {
          input.checked = true;
          last = input;
        }
      });
      if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
    }

    scheduleEnd() {
      const end = Date.parse(this.dataset.ends || '');
      if (!Number.isFinite(end)) return;
      const left = end - Date.now();
      const finish = () => {
        this.querySelectorAll('.kvbp__card').forEach((card) => {
          if (card.dataset.kvbpTier !== '1') card.remove();
        });
        const single = this.querySelector('.kvbp__radio[value="1"]');
        if (single) single.checked = true;
        this.syncTier();
        this.updatePrices();
      };
      if (left <= 0) finish();
      else if (left < 2147483647) this.endTimer = window.setTimeout(finish, left + 500);
    }

    form() {
      return document.getElementById(`product-form-${this.sectionId}`);
    }

    mainVariantId() {
      const form = this.form();
      const input = form && form.querySelector('input[name="id"]');
      return input ? String(input.value) : '';
    }

    unitSelects() {
      return Array.from(this.querySelectorAll('[data-kvbp-unit]'));
    }

    /** Selected variant ids for a multi-buy tier, one per item (defaults to the current tier). */
    unitVariants(count = this.tierQty()) {
      if (this.itemsMode) {
        return this.cardVariants().map((variant) => (variant ? String(variant.id) : ''));
      }
      const selects = this.unitSelects().slice(0, count);
      if (!this.mix || selects.length < count) {
        const id = this.mainVariantId();
        return Array.from({ length: count }, (_, i) => (selects[i] ? String(selects[i].value) : id));
      }
      return selects.map((select) => String(select.value));
    }

    isMixed() {
      if (this.itemsMode) {
        const main = this.mainVariantId();
        return this.unitVariants().some((id) => id !== main);
      }
      if (!this.isMulti()) return false;
      const main = this.mainVariantId();
      return this.unitVariants().some((id) => id !== main);
    }

    /* ---------- events ---------- */

    onChange(event) {
      if (event.target.classList.contains('kvbp__radio')) {
        this.syncTier();
        this.updatePrices();
        if (this.itemsMode) this.pushMain();
        return;
      }
      if (this.itemsMode && event.target.matches('[data-kvbp-opt]')) {
        this.refreshSwatches(event.target.closest('.kvbp__item') || this);
        this.updatePrices();
        this.say('');
        const card = event.target.closest('.kvbp__card');
        if (card && card.dataset.kvbpTier === this.tier()) this.pushMain();
        this.syncPaymentButton();
        return;
      }
      if (event.target.matches('[data-kvbp-unit]')) {
        this.touchedUnits = true;
        this.updatePrices();
        this.syncPaymentButton();
      }
    }

    onMainVariant(variant) {
      if (this.itemsMode) {
        window.setTimeout(() => {
          this.syncQuantity();
          this.syncPaymentButton();
        }, 60);
        return;
      }
      // Until the shopper picks per item, every item follows the main picker.
      if (!this.touchedUnits) {
        this.unitSelects().forEach((select) => {
          if (select.querySelector(`option[value="${variant.id}"]`)) select.value = String(variant.id);
        });
      }
      // Dawn re-renders the quantity block on variant change, so push the tier quantity again.
      window.setTimeout(() => {
        this.syncQuantity();
        this.syncPaymentButton();
      }, 60);
      this.updatePrices(variant.price);
    }

    /** Express checkout buys the main variant only, so hide it while the shopper has mixed variants. */
    syncPaymentButton() {
      const form = this.form();
      const button = form && form.querySelector('.shopify-payment-button');
      if (!button) return;
      button.style.display = this.isMixed() ? 'none' : '';
    }

    syncTier() {
      const tier = this.tier();
      this.querySelectorAll('[data-kvbp-tier]').forEach((label) => {
        label.classList.toggle('is-selected', label.dataset.kvbpTier === tier);
      });
      if (this.itemsMode) {
        this.querySelectorAll('.kvbp__card').forEach((card) => {
          const items = card.querySelector('[data-kvbp-items]');
          if (items) items.hidden = card.dataset.kvbpTier !== tier;
        });
        this.syncQuantity();
        this.syncPaymentButton();
        return;
      }
      if (this.units) this.units.hidden = !this.isMulti(tier);
      const qty = this.tierQty(tier);
      const paid = qty - this.tierFree(tier);
      this.querySelectorAll('[data-kvbp-unit-wrap]').forEach((wrap) => {
        const index = parseInt(wrap.dataset.kvbpUnitWrap, 10);
        wrap.hidden = index > qty;
        const tag = wrap.querySelector('[data-kvbp-free-tag]');
        if (tag) tag.hidden = index <= paid || index > qty;
      });
      this.syncQuantity();
      this.syncPaymentButton();
    }

    syncQuantity() {
      const input = document.getElementById(`Quantity-${this.sectionId}`);
      if (!input) return;
      const qty = String(this.tierQty());
      if (input.value !== qty) {
        input.value = qty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onSubmitCapture(event) {
      const form = this.form();
      if (!form || event.target !== form) return;
      // Same variant for every item (or tier 1): let Dawn handle it, with the right quantity.
      this.syncQuantity();
      if (this.itemsMode) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (this.unitVariants().some((id) => !id)) {
          this.say(this.dataset.msgUnavailable);
          return;
        }
        this.addMixed(form);
        return;
      }
      if (!this.isMixed()) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.addMixed(form);
    }

    /* ---------- prices ---------- */

    money(cents) {
      if (window.KvMoney && typeof window.KvMoney.format === 'function') {
        try {
          return window.KvMoney.format(cents);
        } catch (e) {
          /* fall through */
        }
      }
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      const [whole, dec] = value.split('.');
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withDots},${dec}`)
        .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${dec}`);
    }

    priceOf(variantId, fallback) {
      const option = this.querySelector(`[data-kvbp-unit] option[value="${variantId}"]`);
      const cents = option ? parseInt(option.dataset.price, 10) : NaN;
      return Number.isFinite(cents) ? cents : fallback;
    }

    updatePrices(mainPrice) {
      let base = Number.isFinite(mainPrice) ? mainPrice : this.priceOf(this.mainVariantId(), NaN);
      if (!Number.isFinite(base) && this.itemsMode) {
        const main = this.variants.find((variant) => String(variant.id) === this.mainVariantId()) || this.variants[0];
        base = main ? main.price : NaN;
      }
      if (!Number.isFinite(base)) return;

      const t1 = this.querySelector('[data-kvbp-t1-total]');
      if (t1) t1.textContent = this.money(base * this.q1);

      if (this.itemsMode) {
        this.querySelectorAll('.kvbp__card').forEach((card) => {
          const tier = card.dataset.kvbpTier;
          const free = parseInt(card.dataset.free, 10) || 0;
          const prices = this.cardVariants(tier)
            .map((variant) => (variant ? variant.price : base))
            .sort((a, b) => a - b);
          const full = prices.reduce((sum, p) => sum + p, 0);
          const freeValue = prices.slice(0, free).reduce((sum, p) => sum + p, 0);
          const total = card.querySelector('[data-kvbp-total]');
          const fullEl = card.querySelector('[data-kvbp-full]');
          const saveEl = card.querySelector('[data-kvbp-tsave]');
          if (total) total.textContent = this.money(full - freeValue);
          if (fullEl) fullEl.textContent = this.money(full);
          if (saveEl) saveEl.textContent = this.money(freeValue);
          card.querySelectorAll('[data-kvbp-item]').forEach((item, index) => {
            const variant = this.cardVariants(tier)[index];
            item.classList.toggle('is-unavailable', !variant || !variant.available);
          });
        });
        return;
      }
      // Multi-buy tiers: the automatic discount makes the cheapest item(s) free.
      const tiers = [{ key: '2', qty: this.q2, free: this.free, save: '[data-kvbp-save]' }];
      if (this.q3) tiers.push({ key: '3', qty: this.q3, free: this.free3, save: '[data-kvbp-save3]' });
      tiers.forEach(({ key, qty, free, save }) => {
        const prices = this.unitVariants(qty)
          .map((id) => this.priceOf(id, base))
          .sort((a, b) => a - b);
        const full = prices.reduce((sum, p) => sum + p, 0);
        const freeValue = prices.slice(0, free).reduce((sum, p) => sum + p, 0);
        const total = this.querySelector(`[data-kvbp-t${key}-total]`);
        const fullEl = this.querySelector(`[data-kvbp-t${key}-full]`);
        const saveEl = this.querySelector(save);
        if (total) total.textContent = this.money(full - freeValue);
        if (fullEl) fullEl.textContent = this.money(full);
        if (saveEl) saveEl.textContent = this.money(freeValue);
      });
    }

    /* ---------- mixed add ---------- */

    say(message) {
      if (!this.status) return;
      this.status.hidden = !message;
      this.status.textContent = message || '';
    }

    addMixed(form) {
      if (this.busy) return;
      const quantities = new Map();
      this.unitVariants().forEach((id) => {
        if (id) quantities.set(id, (quantities.get(id) || 0) + 1);
      });
      const items = Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
      if (!items.length) return;

      const button = form.querySelector('[type="submit"][name="add"]');
      const drawer = document.querySelector('cart-drawer');
      const canRender =
        drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function';
      const body = { items };
      if (canRender) {
        body.sections = drawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(button || document.activeElement);
      }

      this.busy = true;
      this.say('');
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }
      const done = () => {
        this.busy = false;
        if (button) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          const spinner = button.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
      };

      const routes = window.routes || {};
      fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          done();
          if (!ok || (data && data.status)) {
            const message = (data && (data.description || data.message)) || this.dataset.msgError;
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, { source: 'kv-bundle-picker', productVariantId: items[0].id, errors: data && (data.errors || data.description), message: data && data.message });
            }
            this.say(message);
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kv-bundle-picker', productVariantId: items[0].id, cartData: data });
          }
          if (canRender && data && data.sections) {
            try {
              drawer.classList.remove('is-empty');
              drawer.renderContents(data);
              return;
            } catch (error) {
              console.error('[kv-bundle] drawer render failed', error);
            }
          }
          window.location = routes.cart_url || '/cart';
        })
        .catch((error) => {
          console.error('[kv-bundle]', error);
          done();
          this.say(this.dataset.msgError);
        });
    }
  }

  customElements.define('kv-bundle-picker', KvBundlePicker);
})();
